// Alert Config React Hooks
// Feature: 015-we-want-to
// T061-T063: React Query hooks for alert configurations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAlertConfigs,
  fetchAlertConfigsByCollection,
  createAlertConfig,
  updateAlertConfig,
  deleteAlertConfig,
  type AlertConfig,
  type CreateAlertConfigInput,
  type UpdateAlertConfigInput,
} from '../api/alertConfigs';

/**
 * Hook to fetch all alert configurations for the authenticated user
 */
export function useAlertConfigs() {
  return useQuery({
    queryKey: ['alertConfigs'],
    queryFn: fetchAlertConfigs,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch alert configurations for a specific collection
 */
export function useAlertConfigsByCollection(collectionId: string | undefined) {
  return useQuery({
    queryKey: ['alertConfigs', 'collection', collectionId],
    queryFn: () => fetchAlertConfigsByCollection(collectionId!),
    enabled: !!collectionId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to create a new alert configuration
 */
export function useCreateAlertConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAlertConfig,
    onSuccess: (data) => {
      // Invalidate all alert config queries to refetch
      queryClient.invalidateQueries({ queryKey: ['alertConfigs'] });

      // Optimistically add to collection-specific query
      const collectionId = data.alert_config.collection_id;
      queryClient.invalidateQueries({
        queryKey: ['alertConfigs', 'collection', collectionId],
      });
    },
  });
}

/**
 * Hook to update an alert configuration
 */
export function useUpdateAlertConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAlertConfigInput }) =>
      updateAlertConfig(id, input),
    onSuccess: (data) => {
      // Invalidate all alert config queries to refetch
      queryClient.invalidateQueries({ queryKey: ['alertConfigs'] });

      // Also invalidate collection-specific query
      const collectionId = data.alert_config.collection_id;
      queryClient.invalidateQueries({
        queryKey: ['alertConfigs', 'collection', collectionId],
      });
    },
  });
}

/**
 * Hook to delete an alert configuration
 */
export function useDeleteAlertConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAlertConfig,
    onSuccess: () => {
      // Invalidate all alert config queries to refetch
      queryClient.invalidateQueries({ queryKey: ['alertConfigs'] });
    },
  });
}

/**
 * Check if user has an alert config for a specific collection and notification type
 */
export function useHasAlertConfig(
  collectionId: string | undefined,
  notificationType: string
): boolean {
  const { data } = useAlertConfigsByCollection(collectionId);

  if (!data?.alert_configs) return false;

  return data.alert_configs.some(
    (config) => config.notification_type === notificationType
  );
}

/**
 * Get alert config for a specific collection and notification type
 */
export function useAlertConfig(
  collectionId: string | undefined,
  notificationType: string
): AlertConfig | undefined {
  const { data } = useAlertConfigsByCollection(collectionId);

  if (!data?.alert_configs) return undefined;

  return data.alert_configs.find(
    (config) => config.notification_type === notificationType
  );
}
